<template>
  <section v-if="display" :class="['product-carousel', themeClass]">
    <div class="product-carousel__header">
      <h2 v-if="title" class="product-carousel__title">
        {{ title }}
      </h2>
      <oie-base-button
        v-if="closable"
        :aria-label="tStatic('close')"
        class="product-carousel__button-icon"
        :class="['button--icon-only']"
        @click="display = false">
        <template slot="icon">
          <oie-use-svg svg="close"></oie-use-svg>
        </template>
      </oie-base-button>
    </div>
    <div v-if="hasProducts" :key="key" :class="['product-carousel__product-wrapper', {'product-carousel__product-wrapper--padded': showSlideButtons}]">
      <div
        :id="`product-carousel__${id}`"
        v-product-slider="{settings, id}"
        class="product-carousel__product-view">
        <!-- Slim product card -->
        <div
          v-for="product in products"
          :key="product.id">
          <div class="product-carousel__card">
            <!-- PRODUCT IMAGE -->
            <div class="product-carousel__card-image-container">
              <oie-base-image
                :images="product.imageUrl ? `${getAssetPath(product.imageUrl)}?preset=ProductSmall&watermark=OIEWatermarkMedium` : `${getAssetPath('/globalassets/missing404/404.png')}?preset=ProductSmall`"
                :alt-text="product.name"
                image-height="4rem"
                image-width="4rem"
                class="product-carousel__card-image"></oie-base-image>
            </div>

            <!-- PRODUCT NAME -->
            <h2 class="product-card__name">
              <a :ref="`${id}-productCarouselLink-${product.id}`" :href="product.url" class="product-card__url product-carousel__link">
                {{ product.name }}
              </a>
            </h2>

            <!-- PRODUCT PRICE -->
            <oie-product-price
              :show-bracket-prices="false"
              :current-object="product"
              :is-product="true"
              class="product-card__price"></oie-product-price>

            <!-- PRODUCT ACTIONS -->

            <!-- buy and add to shopping list button -->
            <div v-if="product.items.length === 1" class="product-carousel__actions">
              <oie-request-quote-slide-in v-if="isQuoteItem(product)" :item-id="product.items[0].id"></oie-request-quote-slide-in>

              <oie-base-action
                v-else
                v-element-loader="{ endpoint: 'addTocart' }"
                class="product-carousel__action"
                theme="secondary"
                size="large"
                :disabled="hasNoPrice(product.items[0])"
                @click.stop="buyProduct(product)">
                {{ tStatic('addToCartButtonLabel') }}
              </oie-base-action>

              <oie-add-to-shopping-list-slide-in :product="product"></oie-add-to-shopping-list-slide-in>
            </div>

            <!-- show articles button -->
            <oie-base-action
              v-if="product.items.length > 1"
              class="product-carousel__action"
              size="large"
              @click="triggerLink(product)">
              {{ tStatic('showArticles') }}
            </oie-base-action>
          </div>
        </div>
      </div>
      <div :id="`product-carousel-controls__${id}`">
        <button
          :class="['product-carousel__navigation product-carousel__navigation--prev']"
          :aria-label="`${tStatic('previous')}`">
          <oie-use-svg svg="caret" class="svg--180"></oie-use-svg>
        </button>

        <button
          :class="['product-carousel__navigation product-carousel__navigation--next']"
          :aria-label="`${tStatic('next')}`">
          <oie-use-svg svg="caret"></oie-use-svg>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import _ from 'lodash'
import { productStatuses } from '@/variables/product-statuses'

export default {
  props: {
    title: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      required: true
    },
    products: {
      type: Array,
      default: () => []
    },
    closable: {
      type: Boolean,
      default: true
    },
    theme: {
      type: String,
      default: 'primary'
    }
  },
  data() {
    return {
      display: true,
      settings: {
        container: `#product-carousel__${this.id}`,
        items: 1,
        slideBy: 1,
        swipeAngle: 45,
        controlsContainer: `#product-carousel-controls__${this.id}`,
        loop: true,
        speed: 500,
        nav: false,
        responsive: {
          640: {
            items: 2,
            slideBy: 2
          },
          767: {
            items: 3,
            slideBy: 3
          },
          992: {
            items: 4,
            slideBy: 4
          },
          1200: {
            items: 5,
            slideBy: 5
          }
        }
      }
    }
  },
  computed: {
    hasProducts() { return this.products.length > 0 },
    showSlideButtons() {
      if(this.mq('x-small') && this.mq('to-small')) {
        return this.products.length > 2
      }
      if(this.mq('small') && this.mq('to-medium')) {
        return this.products.length > 3
      }
      if(this.mq('medium') && this.mq('to-large')) {
        return this.products.length > 4
      }
      if(this.mq('large')) {
        return this.products.length > 5
      }
      return this.products.length > 1
    },
    themeClass() { return `product-carousel--${this.theme}` },
    key() { return _.chain(this.products).map(p => p.id).join('').value() }
  },
  methods: {
    ...mapActions('commerce', ['addToCart']),
    hasNoPrice(article) {
      return article.customerPrice === null || article.customerPrice === undefined || article.customerPrice === 0
    },
    async buyProduct(product) {
      const response = await this.addToCart({ itemId: product.items[0].id, productId: product.id, purchasedOnCategory: product.analyticsCategory, purchasedToVehicle: null })
      if(response.cart) {
        const addedProduct = response.cart.rows.find(x => (x.itemId === product.items[0].id) && (x.productId === product.id))
        this.$emit('buy-product', { product, addedProduct })
      }
    },
    isQuoteItem(product) {
      return product.items[0].publicationStatusCode === productStatuses.noPrice
    },
    triggerLink(product) {
      this.$refs[`${this.id}-productCarouselLink-${product.id}`][0].click()
    }
  }
}
</script>

<style lang="scss">
.product-carousel {
  position: relative;
  background-color: color(white);
  border: 1px solid color(blue-1);
  padding: measure(half);
}

.product-carousel--primary {
  background-color: color(white);
  border: 1px solid color(blue-1);
}

.product-carousel--secondary {
  border: 1px solid color(gray-4)
}

.product-carousel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  @include media(to-medium) {
    flex-direction: row;
    text-align: start;
    align-items: start;
  }
}

.product-carousel__title {
  @include h2-semi-bold;
  margin-top: 0;
  margin-bottom: measure(quarter);
}

.product-carousel__button-icon {
  background-color: transparent;
  height: 2rem;
}

.product-carousel__product-wrapper {
  position: relative;
}

.product-carousel__product-wrapper--padded  {
  padding: 0 2rem;
}

.product-carousel__product-view {
  display: flex;
  padding: measure(eighth);
}

.product-carousel__card {
  position: relative;
  background-color: color(white);
  box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  padding: measure(half);
  margin: 0 measure(eighth);
  min-height: 100%;
}

.product-carousel__card>*:not(:last-child) {
  margin: 0 0 measure(quarter) 0;
}

.product-carousel__card-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.product-carousel__card-image-container .product-carousel__card-image {
  height: 4rem;
  width: 4rem;
}

.product-card__name {
  flex-grow: 1;
}

.product-carousel__link::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.product-carousel__button-icon {
  color: color(gray-2);
  width: 24px;
}

.product-carousel__actions {
  position: relative;
  z-index: 1;
  display: flex;
  gap: measure(quarter);
  align-items: center;
}

.product-carousel__actions >button:first-child {
  flex-grow: 1;
}

.product-carousel__action {
  z-index: 1;
}

.product-carousel__navigation {
  position: absolute;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background-color: color(brand-blue-3);
  color: white;
  border: none;
  transform: translateY(-50%);
  z-index: 1;
  border-radius: 100px;

  &:hover {
    cursor: pointer;
  }
}

.product-carousel__navigation--prev {
  left: 0.25rem;
}

.product-carousel__navigation--next {
  right: 0.25rem;
}

</style>
